import { useState, useEffect, useCallback } from 'react';
import { MetricCard, SectionHeader, StatusBadge, FilterPills, PremiumTable, PageSkeleton, LoadError } from '@/components/dashboard/MetricCard';
import { dashboardApi } from '@/services/dashboardApi';
import { Users, UserCheck, FileCheck, Zap, Search } from 'lucide-react';
import { motion } from 'framer-motion';
import { useSiteLang } from '@/components/site/SiteLang';

const STAGE_LABELS = {
  referred_visit: 'Visita referida',
  test_started: 'Test iniciado',
  test_completed: 'Test completado',
  registration_started: 'Registro iniciado',
  registration_completed: 'Registrado',
  activated: 'Activado',
};

// Inglés británico (05/11/2026)
const STAGE_LABELS_EN = {
  referred_visit: 'Referred visit',
  test_started: 'Test started',
  test_completed: 'Test completed',
  registration_started: 'Sign-up started',
  registration_completed: 'Signed up',
  activated: 'Activated',
};

const T = {
  es: {
    stages: STAGE_LABELS,
    title: 'Mis contactos',
    subtitle: 'Personas que han llegado con tu enlace de invitación',
    loadError: undefined,
    total: 'Total de contactos', testCompleted: 'Test completado', registered: 'Registrados', activated: 'Activados',
    search: 'Buscar por nombre, email o teléfono…',
    all: 'Todos',
    empty: 'Aún no tienes contactos. Comparte tu enlace de invitación (en Inicio o Marketing): cada persona que se registre o haga el test con él aparecerá aquí.',
    noResults: 'Sin resultados para este filtro',
    cols: ['Nombre', 'Email', 'Teléfono', 'Fuente', 'Etapa', 'Test', 'Registro', 'Fecha'],
  },
  en: {
    stages: STAGE_LABELS_EN,
    title: 'My contacts',
    subtitle: 'People who’ve arrived through your invitation link',
    loadError: 'We couldn’t load your contacts.',
    total: 'Total contacts', testCompleted: 'Test completed', registered: 'Signed up', activated: 'Activated',
    search: 'Search by name, email or phone…',
    all: 'All',
    empty: 'No contacts yet. Share your invitation link (from Home or Marketing): everyone who signs up or takes the test through it will appear here.',
    noResults: 'No results for this filter',
    cols: ['Name', 'Email', 'Phone', 'Source', 'Stage', 'Test', 'Sign-up', 'Date'],
  },
};

const STAGE_COLORS = {
  referred_visit: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
  test_started: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  test_completed: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  registration_started: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  registration_completed: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  activated: 'bg-[#c9a84c]/10 text-[#c9a84c] border-[#c9a84c]/20',
};

// 2026-10-09T… → 09/10/2026 (sin pasar por Date para no desfasar el día)
const fmtDate = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '-';
};

function StageBadge({ stage }) {
  const { lang } = useSiteLang();
  const labels = (T[lang] || T.es).stages;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider border ${STAGE_COLORS[stage] || STAGE_COLORS.referred_visit}`}>
      {labels[stage] || stage}
    </span>
  );
}

// embedded: dentro de Comisiones › Pipeline (16/10/2026) la pestaña ya pone el título
export default function DashboardMisContactos({ embedded = false }) {
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const header = embedded ? null : <SectionHeader title={t.title} subtitle={t.subtitle} />;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('Todos');
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    dashboardApi.myContacts()
      .then(setData)
      .catch((e) => { console.error(e); setError(e.message || 'Error'); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <PageSkeleton cards={4} rows={6} />;

  if (error) return (
    <div className="space-y-6" data-testid="dashboard-mis-contactos">
      {header}
      <LoadError onRetry={load} text={t.loadError} />
    </div>
  );

  const contacts = data?.contacts || [];
  const stats = data?.stats || {};
  const stages = ['Todos', ...Object.keys(STAGE_LABELS)];

  const filtered = contacts
    .filter(c => filter === 'Todos' || c.funnel_stage === filter)
    .filter(c => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q) || (c.phone || '').toLowerCase().includes(q);
    });

  return (
    <div className="space-y-6" data-testid="dashboard-mis-contactos">
      {header}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label={t.total} value={stats.total || 0} icon={Users} delay={0} />
        <MetricCard label={t.testCompleted} value={stats.test_completed || 0} icon={FileCheck} delay={1} />
        <MetricCard label={t.registered} value={stats.registered || 0} icon={UserCheck} color="#10b981" delay={2} />
        <MetricCard label={t.activated} value={stats.active || 0} icon={Zap} color="#c9a84c" delay={3} />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t.search}
            className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-[#c9a84c]/30 transition-colors"
            data-testid="contacts-search"
          />
        </div>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-2">
        {stages.map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 ${
              filter === s
                ? 'bg-[#c9a84c] text-[#0a1628] shadow-lg shadow-[#c9a84c]/15'
                : 'bg-white/[0.04] text-gray-400 hover:text-gray-300 hover:bg-white/[0.06] border border-white/[0.06]'
            }`}
          >
            {s === 'Todos' ? t.all : t.stages[s] || s}
          </button>
        ))}
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] py-16 text-center">
            <Users size={32} className="text-gray-400 mx-auto mb-3" />
            <p className="text-gray-400 text-sm max-w-md mx-auto">
              {contacts.length === 0
                ? t.empty
                : t.noResults}
            </p>
          </div>
        ) : (
          <PremiumTable
            cols={t.cols}
            rows={filtered.map(c => [
              c.name || '-',
              c.email || '-',
              c.phone || '-',
              <span className="text-gray-400 text-xs">{c.source || '-'}</span>,
              <StageBadge stage={c.funnel_stage} />,
              c.test_completed ? <StatusBadge status="Completado" /> : <span className="text-gray-400 text-xs">-</span>,
              c.registration_completed ? <StatusBadge status="Completado" /> : <span className="text-gray-400 text-xs">-</span>,
              <span className="text-gray-400 text-xs whitespace-nowrap">{fmtDate(c.created_at)}</span>,
            ])}
          />
        )}
      </motion.div>
    </div>
  );
}
